import { Component } from '@theme/component';
import { prefersReducedMotion, ResizeNotifier } from '@theme/utilities';

const NAV_OVERFLOW_PX = 1;
const NAV_SCROLL_RATIO = 0.75;

/**
 * @typedef {Object} PdpTabsRefs
 * @property {HTMLElement} [nav] - Tab list track
 * @property {HTMLButtonElement} [navPrev] - Previous tabs control
 * @property {HTMLButtonElement} [navNext] - Next tabs control
 */

/**
 * Product information tabs matching the PDP `.pdp-tabs` pattern.
 *
 * @extends {Component<PdpTabsRefs>}
 */
class PdpTabsComponent extends Component {
  /** @type {string | undefined} */
  #selectedTabId;

  /** @type {AbortController | null} */
  #navAbort = null;

  connectedCallback() {
    super.connectedCallback();
    this.#collectTabs();
    this.#bindNavCarousel();
    this.#syncActiveTab({ focus: false });
    this.classList.add('is-ready');
  }

  updatedCallback() {
    super.updatedCallback();
    this.#collectTabs();
    this.#bindNavCarousel();
    this.#syncActiveTab({ focus: false });
  }

  disconnectedCallback() {
    this.#teardownNavCarousel();
    super.disconnectedCallback();
  }

  /**
   * Activates the clicked tab.
   * @param {Event} event
   */
  handleTabClick(event) {
    const tab = this.#eventTab(event);
    if (!tab) return;

    this.#activate(tab, { focus: false });
  }

  /**
   * Keyboard navigation for the tab list (WAI-ARIA tabs).
   * @param {Event} event
   */
  handleTabKeydown(event) {
    const key = 'key' in event && typeof event.key === 'string' ? event.key : '';
    if (!key) return;

    const tabs = this.#tabButtons;
    if (!tabs.length) return;

    const current = this.#eventTab(event) ?? this.#activeTab();
    if (!current) return;

    const index = tabs.indexOf(current);
    if (index === -1) return;

    let nextIndex = -1;

    switch (key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (index + 1) % tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (index - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const next = tabs[nextIndex];
    if (next) this.#activate(next);
  }

  handleNavPrev() {
    this.#scrollNavBy(-1);
  }

  handleNavNext() {
    this.#scrollNavBy(1);
  }

  /**
   * Selects a tab by its block id. Safe to call from other components.
   * @param {string} tabId
   */
  selectTabById(tabId) {
    const tab = this.#tabButtons.find((button) => button.dataset.tabId === tabId);
    if (!tab) return;

    this.#activate(tab, { focus: false });
  }

  /**
   * Moves each tab button into the shared tablist for WAI-ARIA.
   */
  #collectTabs() {
    const tablist = this.#nav;
    if (!tablist) return;

    for (const tab of this.#tabButtons) {
      if (tab.parentElement !== tablist) tablist.append(tab);
    }
  }

  #bindNavCarousel() {
    const tablist = this.#nav;
    if (!tablist) return;

    this.#teardownNavCarousel();
    this.#navAbort = new AbortController();
    const { signal } = this.#navAbort;

    tablist.addEventListener('scroll', () => this.#updateNavOverflow(), { passive: true, signal });
    this.#resizeObserver.observe(tablist);
    this.#updateNavOverflow();
  }

  #teardownNavCarousel() {
    this.#navAbort?.abort();
    this.#navAbort = null;
    this.#resizeObserver.disconnect();
  }

  #updateNavOverflow() {
    const tablist = this.#nav;
    const carousel = tablist?.parentElement;
    if (!tablist || !(carousel instanceof HTMLElement)) return;

    const maxScroll = Math.max(0, tablist.scrollWidth - tablist.clientWidth);
    const canScroll = maxScroll > NAV_OVERFLOW_PX;
    const canPrev = canScroll && tablist.scrollLeft > NAV_OVERFLOW_PX;
    const canNext = canScroll && tablist.scrollLeft < maxScroll - NAV_OVERFLOW_PX;

    carousel.classList.toggle('is-scrollable', canScroll);
    this.#setNavChevron(this.refs.navPrev, canPrev);
    this.#setNavChevron(this.refs.navNext, canNext);
  }

  /**
   * @param {HTMLButtonElement | undefined} button
   * @param {boolean} visible
   */
  #setNavChevron(button, visible) {
    if (!(button instanceof HTMLButtonElement)) return;

    button.hidden = !visible;
    button.disabled = !visible;
    button.tabIndex = visible ? 0 : -1;
  }

  /**
   * @param {-1 | 1} direction
   */
  #scrollNavBy(direction) {
    const tablist = this.#nav;
    if (!tablist) return;

    const amount = Math.max(160, Math.round(tablist.clientWidth * NAV_SCROLL_RATIO));
    const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
    tablist.scrollBy({ left: direction * amount, behavior });
  }

  /**
   * @param {HTMLElement} tab
   */
  #scrollTabIntoView(tab) {
    const tablist = this.#nav;
    if (!tablist || !tablist.contains(tab)) return;

    const trackRect = tablist.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const pad = 8;
    const behavior = prefersReducedMotion() ? 'auto' : 'smooth';

    if (tabRect.left < trackRect.left + pad) {
      tablist.scrollBy({ left: tabRect.left - trackRect.left - pad, behavior });
    } else if (tabRect.right > trackRect.right - pad) {
      tablist.scrollBy({ left: tabRect.right - trackRect.right + pad, behavior });
    }
  }

  /**
   * @returns {HTMLElement | null}
   */
  get #nav() {
    const nav = this.refs.nav;
    if (nav instanceof HTMLElement) return nav;

    const fallback = this.querySelector('.pdp-tabs__nav');
    return fallback instanceof HTMLElement ? fallback : null;
  }

  /**
   * @returns {HTMLButtonElement[]}
   */
  get #tabButtons() {
    /** @type {HTMLButtonElement[]} */
    const tabs = [];

    for (const element of this.querySelectorAll('.pdp-tabs__tab')) {
      if (!(element instanceof HTMLButtonElement)) continue;
      if (element.closest('pdp-tabs-component') !== this) continue;
      tabs.push(element);
    }

    return tabs;
  }

  /**
   * @returns {HTMLElement[]}
   */
  get #panels() {
    /** @type {HTMLElement[]} */
    const panels = [];

    for (const element of this.querySelectorAll('.pdp-tabs__panel')) {
      if (!(element instanceof HTMLElement)) continue;
      if (element.closest('pdp-tabs-component') !== this) continue;
      panels.push(element);
    }

    return panels;
  }

  /**
   * @returns {HTMLButtonElement | undefined}
   */
  #activeTab() {
    return this.#tabButtons.find((tab) => tab.getAttribute('aria-selected') === 'true') ?? this.#tabButtons[0];
  }

  /**
   * @param {Event} event
   * @returns {HTMLButtonElement | null}
   */
  #eventTab(event) {
    const target = event.target;
    if (target instanceof Element) {
      const tab = target.closest('.pdp-tabs__tab');
      if (tab instanceof HTMLButtonElement && tab.closest('pdp-tabs-component') === this) {
        return tab;
      }
    }

    const active = document.activeElement;
    if (active instanceof HTMLButtonElement && active.classList.contains('pdp-tabs__tab')) {
      if (active.closest('pdp-tabs-component') === this) return active;
    }

    return null;
  }

  /**
   * @param {{ focus?: boolean }} [options]
   */
  #syncActiveTab(options = {}) {
    const preferred = this.#selectedTabId
      ? this.#tabButtons.find((tab) => tab.dataset.tabId === this.#selectedTabId)
      : undefined;
    const selected = preferred ?? this.#activeTab();
    if (!selected) return;

    this.#activate(selected, options);
  }

  /**
   * @param {HTMLButtonElement} tab
   * @param {{ focus?: boolean }} [options]
   */
  #activate(tab, options = {}) {
    const { focus = true } = options;
    const tabId = tab.dataset.tabId;
    if (tabId) this.#selectedTabId = tabId;

    for (const button of this.#tabButtons) {
      const selected = button === tab;
      button.setAttribute('aria-selected', selected ? 'true' : 'false');
      button.tabIndex = selected ? 0 : -1;
      button.classList.toggle('is-active', selected);
    }

    for (const panel of this.#panels) {
      const selected = panel.dataset.tabId === tabId;
      panel.hidden = !selected;
      panel.classList.toggle('is-active', selected);
    }

    this.#scrollTabIntoView(tab);
    this.#updateNavOverflow();

    if (!focus) return;

    tab.focus({ preventScroll: true });
  }

  #resizeObserver = new ResizeNotifier(() => this.#updateNavOverflow());
}

if (!customElements.get('pdp-tabs-component')) {
  customElements.define('pdp-tabs-component', PdpTabsComponent);
}
